import { PropsWithChildren, useEffect, useState } from 'react';

import { PropsWithCN, cn } from '@tg-web/utils';

import { prefersReducedMotion } from '../../lib/prefersReducedMotion';

/** Kept in step with the duration class below; the timers read it. */
export const ISLAND_COLLAPSE_MS = 200;

type Status = 'closed' | 'closing' | 'open' | 'opening';

export type IslandCollapseProps = PropsWithChildren &
  PropsWithCN & {
    /**
     * Animate the first expansion too. Off by default, which is what keeps a
     * screen load instant: a wrapper that mounts with the screen renders at its
     * end state, so nothing unfolds on arrival. List items switch it on,
     * because an item appearing in a list that is already on screen is always
     * someone's doing.
     */
    animateOnMount?: boolean;
    /**
     * Also collapse the 12px column gap this element keeps while it has height.
     * For whole islands, which sit in a `gap-3` column — rows inside an island
     * have no gap between them and must leave this off.
     */
    collapseGap?: boolean;
    open: boolean;
  };

/**
 * Height transition for anything that appears or disappears inside an island.
 *
 * `grid-template-rows` between `0fr` and `1fr` is the technique, because it is
 * the only way to animate to and from a content-derived height without
 * measuring anything.
 *
 * Children are unmounted only once the collapse has finished, so an exit
 * animates rather than vanishing — and they are mounted for the whole of an
 * expansion, so anything interactive is usable from the first frame rather
 * than at the end of the 200ms.
 */
export function IslandCollapse({
  animateOnMount = false,
  children,
  className,
  collapseGap = false,
  open,
}: IslandCollapseProps) {
  const [status, setStatus] = useState<Status>(() =>
    open && !animateOnMount ? 'open' : 'closed',
  );
  // Nothing transitions on the frame it is first painted. A panel that is open
  // because the screen loaded that way has no before-state to move from, and
  // this holds whatever a caller passes -- the screen-load rule stops depending
  // on every consumer getting `animateOnMount` right.
  const [hasPainted, setHasPainted] = useState(false);

  useEffect(() => setHasPainted(true), []);

  useEffect(() => {
    const settled: Status = open ? 'open' : 'closed';
    const moving: Status = open ? 'opening' : 'closing';

    setStatus((current) =>
      current === settled ? current : prefersReducedMotion() ? settled : moving,
    );
  }, [open]);

  useEffect(() => {
    if (status !== 'opening' && status !== 'closing') {
      return;
    }
    // A timer rather than transitionend: reduced motion and browsers without
    // animatable grid tracks fire no event, and a panel stuck mid-status would
    // never release its overflow or unmount its children.
    const timer = setTimeout(
      () => setStatus(status === 'opening' ? 'open' : 'closed'),
      ISLAND_COLLAPSE_MS,
    );

    return () => clearTimeout(timer);
  }, [status]);

  const isExpanded = status === 'open' || status === 'opening';

  return (
    <div
      className={cn(
        'grid',
        hasPainted &&
          'transition-[grid-template-rows,margin-bottom] duration-200 ease-out motion-reduce:transition-none',
        isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        collapseGap && !isExpanded && '-mb-3',
        className,
      )}
    >
      {/*
       * min-h-0 is what lets the track shrink past its content — a grid item
       * defaults to min-height:auto, which pins it open.
       *
       * overflow is clipped while anything is moving and released once the
       * panel has settled open. Clipped, an island's blur-shadow-small
       * disappears with it instead of lingering as a line at zero height;
       * released, that same shadow is not sliced off the island's bottom edge.
       */}
      <div className={cn('min-h-0', status !== 'open' && 'overflow-hidden')}>
        {status === 'closed' ? null : children}
      </div>
    </div>
  );
}
